import { useEffect, useRef, useState } from "react";
import { Search, X, Loader2, Sparkles, CornerDownLeft } from "lucide-react";
import { API } from "../auth/api";

const SUGGESTIONS = [
  "Which states have the most T1 customers?",
  "How many contacts are in opportunity or quoting?",
  "Which firms have the most contacts in Texas?",
  "Show me the tier mix across the whole database",
];

const newSession = () =>
  `ask-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const AskPanel = ({ onClose, mobile = false }) => {
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [quota, setQuota] = useState(null);
  const sessionRef = useRef(newSession());
  const endRef = useRef(null);
  const abortRef = useRef(null);

  const loadQuota = async () => {
    try {
      const { data } = await API.get("/ai/quota");
      setQuota(data);
    } catch {
      setQuota(null);
    }
  };

  useEffect(() => {
    loadQuota();
    return () => abortRef.current?.abort();
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, streaming]);

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || streaming) return;
    setQuestion("");
    setError(null);
    setStreaming(true);
    setTurns((t) => [...t, { role: "user", content: q }, { role: "assistant", content: "" }]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const me = await API.get("/auth/me");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/search`, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": me.data?.csrf_token || "",
        },
        body: JSON.stringify({ question: q, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) {
        let detail = `Search failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const data = JSON.parse(payload);
          if (data.error) throw new Error(data.error);
          setTurns((t) => {
            const next = [...t];
            next[next.length - 1] = {
              role: "assistant",
              content: next[next.length - 1].content + (data.delta || ""),
            };
            return next;
          });
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message || "Search failed");
    } finally {
      setStreaming(false);
      loadQuota();
    }
  };

  return (
    <div
      className={
        mobile
          ? "absolute inset-0 flex flex-col border border-violet-400/35 bg-[#0a0a0c]/96 backdrop-blur shadow-2xl"
          : "absolute z-[820] top-4 right-[72px] w-[min(560px,calc(100vw-96px))] max-h-[calc(100vh-120px)] flex flex-col border border-violet-400/35 bg-[#0a0a0c]/96 backdrop-blur shadow-2xl"
      }
      data-testid="ask-panel"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <Sparkles className="w-3.5 h-3.5 text-violet-300" strokeWidth={1.75} />
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-violet-200">
          Ask
        </span>
        <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-zinc-600">
          contacts · firms · tiers
        </span>
        {quota?.limit != null && (
          <span
            className="font-mono text-[10px] tracking-[0.14em] uppercase text-zinc-500"
            data-testid="ask-quota"
          >
            {quota.remaining} left today
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          data-testid="ask-close"
          aria-label="Close search"
          className="ml-auto text-zinc-500 hover:text-zinc-200 transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-600 shrink-0" strokeWidth={1.75} />
          <input
            autoFocus
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="Ask about contacts, firms or territories…"
            data-testid="ask-input"
            className="flex-1 bg-transparent font-mono text-[12px] text-zinc-100 placeholder:text-zinc-600 outline-none"
          />
          <button
            type="button"
            onClick={() => ask()}
            disabled={streaming || !question.trim()}
            data-testid="ask-submit"
            className="shrink-0 px-2 py-1 border border-violet-400/40 text-violet-200 font-mono text-[10px] tracking-[0.14em] uppercase disabled:opacity-30 hover:bg-violet-500/15 transition-colors"
          >
            {streaming ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
            ) : (
              <CornerDownLeft className="w-3.5 h-3.5" strokeWidth={2} />
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4" data-testid="ask-results">
        {turns.length === 0 && (
          <div className="space-y-2" data-testid="ask-suggestions">
            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-zinc-600">
              Try asking
            </div>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => ask(s)}
                className="block w-full text-left border border-white/8 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-zinc-400 hover:border-violet-400/40 hover:text-violet-200 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {turns.map((turn, i) =>
          turn.role === "user" ? (
            <div
              key={i}
              className="font-mono text-[11px] tracking-[0.1em] uppercase text-violet-300/90 border-l-2 border-violet-400/50 pl-2"
              data-testid="ask-question"
            >
              {turn.content}
            </div>
          ) : (
            <div
              key={i}
              className="font-sans text-[13px] leading-relaxed text-zinc-200 whitespace-pre-wrap"
              data-testid="ask-answer"
            >
              {turn.content ||
                (streaming && i === turns.length - 1 ? "Thinking…" : "")}
            </div>
          ),
        )}

        {error && (
          <div
            className="border border-red-400/40 bg-red-500/10 px-2.5 py-2 font-mono text-[11px] text-red-200"
            data-testid="ask-error"
          >
            {error}
          </div>
        )}
        <div ref={endRef} />
      </div>
    </div>
  );
};
